import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Outlet, useLocation } from 'react-router-dom';
import placeholderImage from '../../../assets/placeholder.png';
import { getApiEndpoint, API_BASE_URL } from '../../../config/api';
import './PropertyDashboard.css';
import MainHeader from '../../shared/MainHeader/MainHeader';
import {
  FaEye, FaFire, FaHardHat, FaUsers, FaCogs, FaBolt,
  FaBuilding, FaFirstAid, FaUserTie,
  FaClipboardList, FaCog, FaTimes, FaBars, FaStream
} from 'react-icons/fa';
import PropertyProfileModal from './PropertyProfileModal';
import ComingSoonModal from '../../pages/ComingSoon/ComingSoonModal';
import PropertySearch from '../PropertySearch/PropertySearch';
import AssetDetailModal from '../../compliance/RoofSafety/shared/AssetDetailModal';
import { isViewer, getUserRole } from '../../../utils/roleUtils';

const SHOWCASE_MODE = false;

const getCurrentUserId = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.userId || payload.id || null;
  } catch (_) {
    return null;
  }
};

export default function PropertyDashboard() {
  /* ── State ──────────────────────────────────────────────────── */
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const activeSection = useMemo(() => {
    const path = location.pathname;
    if (path.includes('/roof-safety')) return 'roof';
    if (path.includes('/fire-safety')) return 'fire';
    if (path.includes('/staff-safety')) return 'staff';
    if (path.includes('/equipment-safety')) return 'equipment';
    if (path.includes('/electrical-safety')) return 'electrical';
    if (path.includes('/building-maintenance')) return 'building';
    if (path.includes('/emergency-preparedness')) return 'emergency';
    if (path.includes('/drawings')) return 'drawings';
    if (path.includes('/tours')) return 'tours';
    if (path.includes('/documents')) return 'documents';
    if (path.includes('/inspections')) return 'inspections';
    if (path.includes('/activity')) return 'activity';
    if (path.includes('/logs')) return 'logs';
    if (path.includes('/settings')) return 'settings';
    return 'overview';
  }, [location.pathname]);

  const [property, setProperty] = useState({ name: '', address: '', image_url: null, user_id: null });
  const [imageError, setImageError] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Active sub-section within a module (e.g. 'anchorPoints' within 'roof')
  const [activeSubSection, setActiveSubSection] = useState('dashboard');

  // Document expiry
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [expiryBannerDismissed, setExpiryBannerDismissed] = useState(false);

  // Safety area issue + asset counts
  const [roofSafetyIssues, setRoofSafetyIssues] = useState(0);
  const [roofSafetyAssets, setRoofSafetyAssets] = useState(0);
  const [roofSafetyUntested, setRoofSafetyUntested] = useState(0);
  const [fireSafetyIssues, setFireSafetyIssues] = useState(0);
  const [fireSafetyAssets, setFireSafetyAssets] = useState(0);
  const [fireSafetyUntested, setFireSafetyUntested] = useState(0);
  const [staffSafetyIssues, setStaffSafetyIssues] = useState(0);
  const [staffSafetyAssets, setStaffSafetyAssets] = useState(0);
  const [equipmentSafetyIssues, setEquipmentSafetyIssues] = useState(0);
  const [equipmentSafetyAssets, setEquipmentSafetyAssets] = useState(0);
  const [equipmentSafetyUntested, setEquipmentSafetyUntested] = useState(0);
  const [electricalSafetyIssues, setElectricalSafetyIssues] = useState(0);
  const [electricalSafetyAssets, setElectricalSafetyAssets] = useState(0);
  const [buildingMaintenanceIssues, setBuildingMaintenanceIssues] = useState(0);
  const [buildingMaintenanceAssets, setBuildingMaintenanceAssets] = useState(0);
  const [emergencyPrepIssues, setEmergencyPrepIssues] = useState(0);
  const [emergencyPrepAssets, setEmergencyPrepAssets] = useState(0);

  // Recent inspections
  const [recentInspections, setRecentInspections] = useState([]);

  // Dashboard counters filter state
  const [activeFilters, setActiveFilters] = useState({});

  // Inspection modal
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [availableEquipment, setAvailableEquipment] = useState([]);
  const [inspectionForm, setInspectionForm] = useState({
    inspectionType: 'Routine', equipmentType: '', equipmentId: '', notes: ''
  });

  // Property profile modal
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Coming soon
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [comingSoonFeatureName, setComingSoonFeatureName] = useState('');

  // Property search
  const [showPropertySearch, setShowPropertySearch] = useState(false);

  // Role-based section filtering
  const [allowedSections, setAllowedSections] = useState(null);
  const CONTRACTOR_DEFAULT_SECTIONS = ['overview', 'fire', 'roof', 'staff', 'equipment', 'electrical', 'building', 'emergency', 'documents', 'inspections', 'activity', 'logs'];

  /* ── Menu items ─────────────────────────────────────────────── */
  const mainMenuItems = [
    { id: 'overview',  icon: FaEye,      label: 'Overview' },
    { id: 'roof',      icon: FaHardHat,  label: 'Roof Safety' },
    { id: 'fire',      icon: FaFire,     label: 'Fire Safety' },
    { id: 'staff',     icon: FaUsers,    label: 'Staff Safety' },
    { id: 'equipment', icon: FaCogs,     label: 'Equipment Safety' },
    { id: 'electrical',icon: FaBolt,     label: 'Electrical Safety' },
    { id: 'building',  icon: FaBuilding, label: 'Building Maintenance' },
    { id: 'emergency', icon: FaFirstAid, label: 'Emergency Preparedness' },
  ];

  const bottomMenuItems = [
    { id: 'management', icon: FaUserTie,        label: 'View Management' },
    { id: 'profile',    icon: FaClipboardList,  label: 'Property Profile' },
    { id: 'settings',   icon: FaCog,            label: 'Settings' },
  ];

  /* ── Section filtering (role-based) ─────────────────────────── */
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(getApiEndpoint('/api/users/allowed-sections'), { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : { allowedSections: [] })
      .then(data => {
        const sections = data.allowedSections || [];
        const role = getUserRole();
        const accountType = localStorage.getItem('accountType');
        const isContractorUser = role === 'contractor' || ['contractor_admin', 'contractor_staff'].includes(accountType || '');
        if (isContractorUser && (!Array.isArray(sections) || sections.length === 0)) {
          setAllowedSections(CONTRACTOR_DEFAULT_SECTIONS);
        } else {
          setAllowedSections(sections);
        }
      })
      .catch(() => {
        const role = getUserRole();
        const accountType = localStorage.getItem('accountType');
        const isContractorUser = role === 'contractor' || ['contractor_admin', 'contractor_staff'].includes(accountType || '');
        setAllowedSections(isContractorUser ? CONTRACTOR_DEFAULT_SECTIONS : []);
      });
  }, []);

  const userRole = getUserRole();
  const accountType = localStorage.getItem('accountType');
  const currentUserId = getCurrentUserId();
  const canEditPropertyProfile = ['super_admin', 'cloud_staff'].includes(userRole)
    || localStorage.getItem('internalStaffAccount') === 'true'
    || (property.user_id && currentUserId && String(property.user_id) === String(currentUserId));
  const isContractorLinked = userRole === 'contractor' || ['contractor_admin', 'contractor_staff'].includes(accountType || '');
  const isRestrictedRole = isContractorLinked || ['staff', 'fm', 'admin', 'building_manager'].includes(userRole);
  const sectionsLoaded = allowedSections !== null;
  const hasRestrictions = Array.isArray(allowedSections) && allowedSections.length > 0;
  const showAllSections = !hasRestrictions && (!isRestrictedRole || sectionsLoaded);

  const filterByAllowed = (items) => {
    if (showAllSections) return items;
    if ((allowedSections || []).length > 0) return items.filter(i => allowedSections.includes(i.id));
    if (isRestrictedRole && !sectionsLoaded) return [];
    return items;
  };

  const filteredMainMenuItems = filterByAllowed(mainMenuItems);
  const filteredBottomMenuItems = filterByAllowed(bottomMenuItems);

  /* ── Safety categories (unified) ────────────────────────────── */
  const safetyCategories = [
    { id: 'roof',       name: 'Roof Safety',            assets: roofSafetyAssets,           issues: roofSafetyIssues,          untested: roofSafetyUntested },
    { id: 'fire',       name: 'Fire Safety',            assets: fireSafetyAssets,           issues: fireSafetyIssues,          untested: fireSafetyUntested },
    { id: 'staff',      name: 'Staff Safety',           assets: staffSafetyAssets,          issues: staffSafetyIssues,          untested: 0 },
    { id: 'equipment',  name: 'Equipment Safety',       assets: equipmentSafetyAssets,      issues: equipmentSafetyIssues,     untested: equipmentSafetyUntested },
    { id: 'electrical', name: 'Electrical Safety',      assets: electricalSafetyAssets,     issues: electricalSafetyIssues,    untested: 0 },
    { id: 'building',   name: 'Building Maintenance',   assets: buildingMaintenanceAssets,  issues: buildingMaintenanceIssues, untested: 0 },
    { id: 'emergency',  name: 'Emergency Preparedness', assets: emergencyPrepAssets,        issues: emergencyPrepIssues,       untested: 0 },
  ];

  const filteredSafetyCategories = showAllSections
    ? safetyCategories
    : (allowedSections || []).length > 0
      ? safetyCategories.filter(cat => allowedSections.includes(cat.id))
      : isRestrictedRole && !sectionsLoaded ? [] : safetyCategories;

  /* ── Data fetching — issues ─────────────────────────────────── */
  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const safetyAreas = [
      { area: 'roof-safety',            setter: setRoofSafetyIssues,        untestedSetter: setRoofSafetyUntested },
      { area: 'fire-safety',            setter: setFireSafetyIssues,        untestedSetter: setFireSafetyUntested },
      { area: 'staff-safety',           setter: setStaffSafetyIssues },
      { area: 'equipment-safety',       setter: setEquipmentSafetyIssues,   untestedSetter: setEquipmentSafetyUntested },
      { area: 'electrical-safety',      setter: setElectricalSafetyIssues },
      { area: 'building-maintenance',   setter: setBuildingMaintenanceIssues },
      { area: 'emergency-preparedness', setter: setEmergencyPrepIssues },
    ];

    const fetchAllIssues = async () => {
      for (const { area, setter, untestedSetter } of safetyAreas) {
        try {
          const r = await fetch(getApiEndpoint(`/api/properties/${id}/${area}/issues-count`), { headers });
          if (r.ok) {
            const d = await r.json();
            setter(d.count || 0);
            if (untestedSetter) untestedSetter(d.untested || 0);
          } else {
            setter(0);
            if (untestedSetter) untestedSetter(0);
          }
        } catch {
          setter(0);
          if (untestedSetter) untestedSetter(0);
        }
      }
    };
    fetchAllIssues();

    /* ── Asset counts ─────────────────────────────────────────── */
    const sumEndpoints = async (endpoints, setter) => {
      try {
        const responses = await Promise.all(
          endpoints.map(u => fetch(getApiEndpoint(u), { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ ok: false })))
        );
        const arrays = await Promise.all(responses.map(r => r.ok ? r.json() : []));
        setter(arrays.reduce((s, a) => s + (Array.isArray(a) ? a.length : 0), 0));
      } catch { setter(0); }
    };

    const fetchRoofAssets = () => sumEndpoints([
      `/api/properties/${id}/anchor-points`, `/api/properties/${id}/cradles`,
      `/api/properties/${id}/abseil-systems`, `/api/properties/${id}/ladders`,
      `/api/properties/${id}/walkways`, `/api/properties/${id}/static-lines`,
      `/api/properties/${id}/handrails`
    ], setRoofSafetyAssets);

    const fetchFireAssets = () => sumEndpoints([
      `/api/properties/${id}/fire-safety/fire-extinguishers`, `/api/properties/${id}/fire-safety/smoke-detectors`,
      `/api/properties/${id}/fire-safety/fire-alarms`, `/api/properties/${id}/fire-safety/emergency-lights`,
      `/api/properties/${id}/fire-safety/fire-doors`, `/api/properties/${id}/fire-safety/sprinklers`,
      `/api/properties/${id}/fire-safety/heat-detectors`
    ], setFireSafetyAssets);

    const fetchEquipmentAssets = async () => {
      try {
        const r = await fetch(getApiEndpoint(`/api/properties/${id}/fixed-equipment`), { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) { const d = await r.json(); setEquipmentSafetyAssets(Array.isArray(d) ? d.length : 0); }
        else setEquipmentSafetyAssets(0);
      } catch { setEquipmentSafetyAssets(0); }
    };

    const fetchElectricalAssets = () => sumEndpoints([
      `/api/properties/${id}/electrical-safety/electrical-equipment`,
      `/api/properties/${id}/electrical-safety/pat-testing`,
      `/api/properties/${id}/electrical-safety/rcd-testing`
    ], setElectricalSafetyAssets);

    const fetchStaffAssets = () => sumEndpoints([
      `/api/properties/${id}/staff-safety/staff`,
      `/api/properties/${id}/staff-safety/certifications`,
      `/api/properties/${id}/staff-safety/training-records`
    ], setStaffSafetyAssets);

    const fetchBuildingAssets = () => sumEndpoints([
      `/api/properties/${id}/building-maintenance/hvac`,
      `/api/properties/${id}/building-maintenance/plumbing`,
      `/api/properties/${id}/building-maintenance/facilities`
    ], setBuildingMaintenanceAssets);

    const fetchEmergencyAssets = () => sumEndpoints([
      `/api/properties/${id}/emergency-preparedness/evacuation-plans`,
      `/api/properties/${id}/emergency-preparedness/assembly-points`,
      `/api/properties/${id}/emergency-preparedness/emergency-equipment`
    ], setEmergencyPrepAssets);

    fetchRoofAssets(); fetchFireAssets(); fetchEquipmentAssets();
    fetchElectricalAssets(); fetchStaffAssets(); fetchBuildingAssets(); fetchEmergencyAssets();

    const handleAssetChange = () => {
      fetchRoofAssets(); fetchFireAssets(); fetchEquipmentAssets();
      fetchElectricalAssets(); fetchStaffAssets(); fetchBuildingAssets(); fetchEmergencyAssets();
    };
    const events = ['anchorPointStatusChanged', 'cradleStatusChanged', 'roofSafetyUpdated', 'fireSafetyUpdated', 'equipmentUpdated', 'staffSafetyUpdated'];
    events.forEach(e => window.addEventListener(e, handleAssetChange));
    return () => events.forEach(e => window.removeEventListener(e, handleAssetChange));
  }, [id]);

  /* ── Property details ───────────────────────────────────────── */
  useEffect(() => {
    if (!id) return;
    const fetchPropertyDetails = async () => {
      try {
        const r = await fetch(getApiEndpoint(`/api/properties/${id}`), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
        });
        if (!r.ok) throw new Error(r.status === 404 ? 'Property not found' : 'Failed to fetch');
        const data = await r.json();
        setProperty({ id: data.id, name: data.name, address: data.address, image_url: data.image_url, user_id: data.user_id, property_code: data.property_code });
        localStorage.setItem('selectedPropertyName', data.name);

        const stored = localStorage.getItem('recentProperties');
        let recent = stored ? JSON.parse(stored) : [];
        recent = recent.filter(p => p.id !== parseInt(id));
        recent.unshift({ id: parseInt(id), name: data.name, address: data.address, image_url: data.image_url });
        recent = recent.slice(0, 3);
        localStorage.setItem('recentProperties', JSON.stringify(recent));
        localStorage.setItem('recentPropertiesUpdated', Date.now().toString());
      } catch (err) { console.error('Error fetching property:', err); }
    };
    fetchPropertyDetails();
  }, [id]);

  /* ── Log property view ──────────────────────────────────────── */
  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(getApiEndpoint(`/api/properties/${id}/view`), { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  }, [id]);

  /* ── Recent inspections ─────────────────────────────────────── */
  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem('token');
    fetch(getApiEndpoint(`/api/properties/${id}/recent-inspections`), { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => setRecentInspections(data))
      .catch(() => setRecentInspections([]));
  }, [id]);

  /* ── Expiring documents ─────────────────────────────────────── */
  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem('token');
    fetch(getApiEndpoint(`/api/properties/${id}/documents`), { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(docs => {
        if (!Array.isArray(docs)) return;
        const now = new Date();
        const in30 = new Date(now.getTime() + 30 * 86400000);
        setExpiringDocs(docs.filter(d => { if (!d.expiry_date) return false; const e = new Date(d.expiry_date); return e >= now && e <= in30; }));
      })
      .catch(() => {});
  }, [id]);

  /* ── Navigation ─────────────────────────────────────────────── */
  // Safety modules not yet implemented — clicking them opens the Coming Soon modal
  // with a link to the roadmap, rather than navigating to an empty page.
  const COMING_SOON_MODULES = {
    fire:       'Fire Safety',
    staff:      'Staff Safety',
    equipment:  'Equipment Safety',
    electrical: 'Electrical Safety',
    building:   'Building Maintenance',
    emergency:  'Emergency Preparedness',
  };

  const handleNavigation = (path) => {
    const base = location.pathname.startsWith('/properties/')
      ? `/properties/${id}`
      : `/propertydashboard/${id}`;
    setMobileDrawerOpen(false);
    setActiveSubSection('dashboard');

    if (COMING_SOON_MODULES[path]) {
      setComingSoonFeatureName(COMING_SOON_MODULES[path]);
      setShowComingSoonModal(true);
      return;
    }

    switch (path) {
      case 'overview':   navigate(base); break;
      case 'roof':       navigate(`${base}/roof-safety`); break;
      case 'security':   navigate(`${base}/security-access`); break;
      case 'health':     navigate(`${base}/health-hygiene`); break;
      case 'drawings':   navigate(`${base}/drawings`); break;
      case 'tours':      navigate(`${base}/tours`); break;
      case 'documents':  navigate(`${base}/documents`); break;
      case 'inspections':navigate(`${base}/inspections`); break;
      case 'activity':   navigate(`${base}/activity`); break;
      case 'logs':       navigate(`${base}/logs`); break;
      case 'settings':   navigate(`${base}/settings`); break;
      case 'profile':    setShowProfileModal(true); break;
      case 'management': navigate(`${base}/settings/management`); break;
      default:           navigate(base);
    }
  };

  /* ── Inspection handlers ────────────────────────────────────── */
  const fetchAvailableEquipment = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(getApiEndpoint(`/api/properties/${id}/equipment`), { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setAvailableEquipment(d.equipment || []); }
    } catch {}
  };

  const handleStartInspection = () => {
    if (isViewer()) return;
    if (SHOWCASE_MODE) { setComingSoonFeatureName('New Inspection'); setShowComingSoonModal(true); return; }
    setShowInspectionModal(true);
    fetchAvailableEquipment();
  };

  const handleInspectionSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(getApiEndpoint(`/api/properties/${id}/inspections`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(inspectionForm)
      });
      if (r.ok) {
        setInspectionForm({ inspectionType: 'Routine', equipmentType: '', equipmentId: '', notes: '' });
        setShowInspectionModal(false);
        // Refresh recent inspections
        const r2 = await fetch(getApiEndpoint(`/api/properties/${id}/recent-inspections`), { headers: { Authorization: `Bearer ${token}` } });
        if (r2.ok) setRecentInspections(await r2.json());
      } else {
        const err = await r.json();
        console.error('Inspection error:', err);
      }
    } catch (err) { console.error('Inspection error:', err); }
  };

  const handleInspectionFormChange = (field, value) => {
    setInspectionForm(prev => ({ ...prev, [field]: value }));
  };

  /* ── Image helper ───────────────────────────────────────────── */
  const getImageUrl = (imageUrl) => {
    if (!imageUrl || imageError) return placeholderImage;
    if (imageUrl.startsWith('http')) return imageUrl;
    return imageUrl.startsWith('uploads/')
      ? getApiEndpoint(`/api/${imageUrl}`)
      : getApiEndpoint(`/api/uploads/${imageUrl}`);
  };

  /* ── Scroll main content to top on route change ──────────────── */
  const mainRef = useRef(null);
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [location.pathname]);

  /* ── Close drawer on outside click / Escape ─────────────────── */
  useEffect(() => {
    if (!mobileDrawerOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMobileDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileDrawerOpen]);

  /* ── Asset history modal (opens instantly from search) ───────── */
  const [historyAsset, setHistoryAsset] = useState(null);
  const [historyAssetType, setHistoryAssetType] = useState(null);
  useEffect(() => {
    const onOpenHistory = (e) => {
      const { asset, assetType } = e.detail || {};
      if (asset && assetType) {
        setHistoryAsset(asset);
        setHistoryAssetType(assetType);
      }
    };
    window.addEventListener('openAssetHistory', onOpenHistory);
    return () => window.removeEventListener('openAssetHistory', onOpenHistory);
  }, []);

  /* ── Property search (Cmd+K + header icon) ────────────────────── */
  useEffect(() => {
    const openSearch = () => setShowPropertySearch(true);
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowPropertySearch(prev => !prev);
      }
    };
    const onSearchNavigate = (e) => {
      const { subSection } = e.detail || {};
      if (subSection) setActiveSubSection(subSection);
    };
    window.addEventListener('openPropertySearch', openSearch);
    window.addEventListener('propertySearchNavigate', onSearchNavigate);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('openPropertySearch', openSearch);
      window.removeEventListener('propertySearchNavigate', onSearchNavigate);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  /* ════════════════════════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════════════════════════ */
  return (
    <div className="pd-page">
      <MainHeader />

      {/* ── Mobile drawer toggle ─────────────────────────────────── */}
      <button
        className="pd-drawer-toggle"
        onClick={() => setMobileDrawerOpen(v => !v)}
        aria-label="Toggle safety modules menu"
      >
        <FaBars />
        <span>Modules</span>
      </button>

      {/* ── Backdrop (mobile drawer) ─────────────────────────────── */}
      {mobileDrawerOpen && (
        <div className="pd-drawer-backdrop" onClick={() => setMobileDrawerOpen(false)} />
      )}

      {/* ── Sidebar / Drawer ─────────────────────────────────────── */}
      <aside className={`pd-sidebar ${mobileDrawerOpen ? 'pd-sidebar-open' : ''}`}>
        <div className="pd-sidebar-header">
          <span className="pd-sidebar-heading">Safety Modules</span>
          <button className="pd-sidebar-close" onClick={() => setMobileDrawerOpen(false)} aria-label="Close menu">
            <FaTimes />
          </button>
        </div>

        <nav className="pd-sidebar-nav">
          {filteredMainMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                className={`pd-sidebar-item ${isActive ? 'pd-sidebar-active' : ''}`}
                onClick={() => handleNavigation(item.id)}
              >
                <Icon className="pd-sidebar-icon" />
                <span className="pd-sidebar-label">{item.label}</span>
                {isActive && <span className="pd-sidebar-indicator" />}
              </button>
            );
          })}
        </nav>

        <div className="pd-sidebar-bottom">
          <div className="pd-sidebar-image">
            <img
              src={getImageUrl(property.image_url)}
              alt={property.name || 'Property'}
              onError={(e) => { setImageError(true); e.target.src = placeholderImage; }}
            />
          </div>
          <button
            className={`pd-sidebar-item ${activeSection === 'activity' ? 'pd-sidebar-active' : ''}`}
            onClick={() => handleNavigation('activity')}
          >
            <FaStream className="pd-sidebar-icon" />
            <span className="pd-sidebar-label">Activity</span>
          </button>
          {filteredBottomMenuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={`pd-sidebar-item ${activeSection === item.id ? 'pd-sidebar-active' : ''}`}
                onClick={() => handleNavigation(item.id)}
              >
                <Icon className="pd-sidebar-icon" />
                <span className="pd-sidebar-label">{item.label}</span>
              </button>
            );
          })}
        </div>
      </aside>

      {/* ── Main Content ─────────────────────────────────────────── */}
      <main className="pd-main" ref={mainRef}>
        <Outlet context={{
          property, id, expiringDocs, expiryBannerDismissed, setExpiryBannerDismissed,
          filteredSafetyCategories, handleNavigation, recentInspections,
          activeFilters, setActiveFilters, allowedSections, hasRestrictions,
          showInspectionModal, setShowInspectionModal, availableEquipment,
          inspectionForm, handleInspectionFormChange, handleInspectionSubmit,
          handleStartInspection, navigate,
          activeSubSection, setActiveSubSection
        }} />
      </main>

      {/* Property Profile Modal */}
      <PropertyProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        propertyId={id}
        readOnly={!canEditPropertyProfile}
        initialValues={{
          name: property.name,
          address: property.address,
          image_url: property.image_url && property.image_url.startsWith('http')
            ? property.image_url.replace(`${API_BASE_URL}/api/`, '')
            : (property.image_url || '')
        }}
        onUpdated={(updated) => setProperty({
          id: updated.id,
          name: updated.name,
          address: updated.address,
          image_url: updated.image_url,
          user_id: updated.user_id,
          property_code: updated.property_code
        })}
      />

      {/* Coming Soon */}
      <ComingSoonModal isOpen={showComingSoonModal} onClose={() => setShowComingSoonModal(false)} featureName={comingSoonFeatureName} />

      {/* Property Search */}
      <PropertySearch
        isOpen={showPropertySearch}
        onClose={() => setShowPropertySearch(false)}
        propertyId={id}
        allowedSections={hasRestrictions ? allowedSections : null}
      />

      {/* Instant asset history popup (dispatched from PropertySearch) */}
      <AssetDetailModal
        isOpen={!!historyAsset}
        onClose={() => setHistoryAsset(null)}
        asset={historyAsset}
        assetType={historyAssetType}
        propertyId={id}
      />
    </div>
  );
}
